import {UNASSIGNED} from '@modules'
import {CatalogStore, ui} from '@stores'
import {IDeleteImagePayload, IEditableDetails, IFieldMutationPayLoad} from '@stores/elasticsearch/mutations'
import {authStore} from '@utils'
import axios, {CancelToken} from 'axios'
import {action, computed, observable, when} from 'mobx'
import {computedFn, _} from '@api'
import {ElasticSearchClient, esClient, IGetOptions, IUpdateDocOptions} from './ElasticSearchClient'

export const MAX_QUERY_SIZE = 30000
export const DEBUG_ES = DEBUG && true

export type CentricId = string
export type ElasticSearchId = string
export type EsSearchBatchOptions = { ms?: number } | false
export const defaultBatchOptions: EsSearchBatchOptions = {ms: 100}
export type EsLogParam = { log?: { query?: boolean, result?: boolean } }
export type EsSearchHandler = <T = {}>(params: { body: any, index?: string, size?: number, meta?: any, cancelToken?: CancelToken, batch?: boolean }, {log}?: EsLogParam) => Promise<Partial<EsSearchResponse<T>>>
export type ElasticSearchIndex = string
export type EsQueryClause = any

export type CatalogItemEs<T = any> = {
  _id: String
} & T

export type C8ImageEs = {
  image?: {
    name?: string,
    id?: string
  }
  id: string
  label?: string
}

export type EsHitResult<T = {}> = { _id?: string, _source: T & any, _index?: any, inner_hits?: T & any }

export type HitsResult<T = {}> = {
  hits: Array<EsHitResult<T>>
  total?: { value: number, relation: 'eq' | 'gt' }
}

export const vipESIndexes = {
  config: 'vip-config',
  customAttributeDef: 'vlp-custom-attribute-definition',
  customAttribute: 'custom-attribute',
  databaseConfig: 'vip-database-config',
  tag: 'vip-tag',
  tagType: 'vlp-tag-type',
  deployment: 'vip-deployment',
  changeHistory: 'vlp-event',
  presets: 'vip-presets',
  user: 'vlp-user',
  reports: 'vip-reports',
  userSettings: 'vlp-user-settings',
  comment: 'vlp-comment',
  vlpMerchCollection: 'vlp-merch-collection',
  performanceLog: 'vip-performance-log',
  changeLogIndexes: 'vlp-*-history,-vlp-event-history',
  changeHistoryIndexes: 'vlp-*-history,-vlp-event-history',
  vipAWSConfig: 'vip-aws-config',
  assortmentOverrides: 'vip-assortment-overrides',
  pdfArtifact: 'vip-pdf-artifact'
}

export const emptyAggregationFilter = {filter: {bool: {}}}

export type IAggregationResult<T = { [name: string]: IAggregationResult<any> }> = {
  doc_count: number
  aggs?: T
} & any

export interface EsSearchResponse<T = {}> {
  aggregations: {
    [key: string]: IAggregationResult
  },
  timed_out: boolean,
  hits: HitsResult<T>
  error?
  name?: string
}

export interface MESSearchResponses {
  responses?: Array<EsSearchResponse>,
  took?: number

  timed_out: boolean,
  error?
}

export interface EsGetResponse {
  _source: any
}

export interface IUpsertInput {
  index?: string,
  value: any
}

export interface ESDeleteResponse {
  batches: number
  deleted: number
  failures: Array<any>
  noops: number
  timed_out: boolean
  version_conflicts: number
  took: number
}

export interface IVipTag {
  name: string,
  type: string,
  description: string | null,
  index: string
}

export interface IDeleteVipTag {
  id: string,
  name?: string,
  type: string,
  index: string
}

export interface IUpdateVipTag {
  id: string
  name: string,
  type: string,
  description: string | null,
  index: string
}

export interface ICompositeResult {
  after_key: { [key: string]: any }
  buckets: Array<ICompositeBucketResult>
}

export interface ICompositeBucketResult {
  key: { [group: string]: any }
  doc_count: number

  [agg: string]: ICompositeResult | IAggregationResult
}

export interface IReadFieldResponse {
  val: any,
  valAsString: string
}

export class ElasticSearchStore {
  constructor(public catalog: CatalogStore) {
  }

  @computed get clientUrl() {
    return this.axiosInstance.defaults.baseURL
  }

  get axiosInstance() { return this.client?.client}

  get axiosServicesInstance() { return this.client?.axiosNest ?? this.client?.axiosServicesInstance}

  @action mutate = async ({toIndex, toId, docMapping, idField, changes}: IEditableDetails) => {
    try {
      const response = await this.axiosInstance.post('/es/mutate', {
        toIndex,
        toId,
        docMapping,
        idField,
        changes: changes
      })
      //console.log(response, 'es mutation response')
      return response
    } catch (error) {
      console.error(error)
    }
  }

  get client() { return esClient }

  @computed get isCloudES() {
    const {stores: {config: {config}}} = this
    if (config.esUrl && config.esUrl.includes(':9243')) return true
    return false
  }

  _itemById = observable.map<string, any>({}, {deep: false})
  _loadingById = observable.set<string>()

  getItemById = computedFn(({index, id, meta}: IGetOptions) => {
    const {_itemById, _loadingById} = this

    if (!index) throw new Error(`Index is required`)

    const key = `${index}-${id}`,
      existing = _itemById.get(key)

    if (!existing) {
      if (!_loadingById.has(key)) {
        this.get({index, id, meta}, {log: {query: false}})
      }
    }

    return existing
  })

  loadItemsByIds = async ({ids, index}: { ids: Array<string>, index?: string }) => {
    const {catalog, search, unwrapHitsResult, _itemById} = this,
      idsToLoad = ids.filter(_.identity)

    if (_.isEmpty(idsToLoad)) {

    }
    else {
      const response = await search({
          index: index ?? catalog.index,
          body: {
            query: {
              terms: {
                id: ids
              }
            }
          },
          size: ids.length,
          meta: `Get ${index} items by ids`
        }),
        {items} = unwrapHitsResult(response)

      items.map(item => {
        const id = catalog.idFor(item),
          key = `${index}-${id}`
        if (!_itemById.has(key)) {
          _itemById.set(key, item)
        }
      })
    }
  }

  get = async <T = any>({index, id, _source, ignoreError, meta}: IGetOptions, {log}: Partial<{ log: { query?, result? } }> = {}): Promise<T> => {
    const {catalog} = this
    if (!index && !catalog?.index) throw new Error(`ElasticSearch:  _get requires an index to be set or an active catalog workspace`)
    // console.trace({index, id, meta})
    return new Promise(async (res, rej) => {
      when(() => this.client != null, async () => {
        const {_itemById, _loadingById} = this,
          key = `${index}-${id}`
        _loadingById.add(key)

        log?.query && console.debug(`GET ${index ?? catalog?.index}/_doc/${id}`)

        // if (!stores.authStore.userIsAuthenticated) {
        //   console.warn(`User is not logged in, no ES access allowed`)
        //   return {aggregations: {}, timed_out: false, hits: {hits: [], total: {value: 0, relation: 'eq'}}}
        // }

        try {
          const r = await this.client.get({index, id, _source, ignoreError, meta})

          log?.result && console.debug(r)
          if (r?.error) { throw new Error(r.error)}

          _itemById.set(key, r)
          _loadingById.delete(key)

          res(r)
        } catch (err) {
          if (!ignoreError) {
            // Log the query if not done so already
            !log?.query && console.error(`Error running ES query:`, {query: `GET ${index ?? catalog?.index}/_doc/${id}`}, {meta})
            console.error(err)
            // catalog && (catalog.error = err)
            rej(err)
          }
          res(null)
        }
      })
    })
  }

  msearch = async ({body, size, meta, ...props}: { body: any, index?: string, size?: number, meta?: any }, {log}: Partial<{ log: { query?, result? } }> = {}): Promise<Partial<MESSearchResponses>> => {
    if (_.isEmpty(body)) {
      console.trace(`Empty msearch request`)
      return {}
    }

    if (!meta) {
      DEBUG && console.warn(`Please supply a 'meta' property to describe yourself uniquely.`)
    }

    const {catalog} = this

    return new Promise((res, rej) => {
      when(() => this.client != null, async () => {
        const index: string = props.index ? props.index : props.index === undefined ? _.first(body?.query?.bool?.filter?.map(v => v?.term?._index))
                                                                                    : null

        let inConsoleGroup = false

        if (DEBUG_ES || log?.query) {
          inConsoleGroup = true
          console.groupCollapsed(`GET ${index ? `${index}/` : ''}_msearch${meta ? `  - ${meta}` : ''}`)
          console.debug(`GET ${index ? `${index}/` : ''}_msearch\n${JSON.stringify(body, null, 2)}`)
        }

        // if (!stores.authStore.userIsAuthenticated) {
        //   console.warn(`User is not logged in, no ES access allowed`)
        //   return {aggregations: {}, timed_out: false, hits: {hits: [], total: {value: 0, relation: 'eq'}}}
        // }

        try {
          const searchOptions = {
              filter_path: ['hits', 'aggregations'],
              index,
              size,
              body,
              meta
            },
            runSearch = async () => {
              const r = await this.client.msearch(searchOptions) as Partial<MESSearchResponses>

              log?.result && console.debug(r)

              if (r.error) { throw new Error(r.error)}
              return r
            }

          if (inConsoleGroup) console.groupEnd()
          const r = await runSearch()

          res(r)
        } catch (err) {
          if (!axios.isCancel(err)) {
            // Log the query if not done so already
            !log?.query && console.trace(`GET ${index ?? catalog?.index}/_msearch\n${JSON.stringify(body, null, 2)}`)
            console.error(err)
          }
          rej(err)
        } finally {

        }
      })
    })
  }

  search: EsSearchHandler = (props, {log} = {}) => {
    if (!authStore.userIsAuthenticated) {
      //throw new Error(`User is not authenticated`)
      return null
    }

    if (!props.meta) {
      DEBUG && console.warn(`Please supply a 'meta' property to describe the search uniquely.`)
    }

    const {catalog} = this
    return new Promise((res, rej) => {
      when(() => this.client != null, async () => {
        const {body, size, cancelToken, batch} = props,
          index: string = props.index ? props.index : props.index === undefined ? _.first(body?.query?.bool?.filter?.map(v => v?.term?._index)) : null

        let meta = props.meta
        if (meta) { meta = `${catalog.debugId} - ${meta}` }

        let inConsoleGroup = false

        if (log?.query) {
          inConsoleGroup = true
          console.groupCollapsed(`GET ${index ? `${index}/` : ''}_search${meta ? `  - ${meta}` : ''}`)
          console.debug(`GET ${index ? `${index}/` : ''}_search\n${JSON.stringify(body, null, 2)}`)
        }

        const searchOptions = {
          filter_path: ['hits', 'aggregations'],
          index,
          size,
          body,
          meta,
          cancelToken,
          batch
        }

        // if (!stores.authStore.userIsAuthenticated) {
        //   console.warn(`User is not logged in, no ES access allowed`)
        //   return {aggregations: {}, timed_out: false, hits: {hits: [], total: {value: 0, relation: 'eq'}}}
        // }

        try {
          const runSearch = async () => {
            const r = await this.client.search(searchOptions) as Partial<EsSearchResponse>

            log?.result && console.debug(r)

            if (r.error) {
              console.error(r.error)
              throw new Error(r.error.reason)
            }

            return r
          }

          if (inConsoleGroup) console.groupEnd()
          const r = await runSearch()
          res(r)
        } catch (err) {
          // Log the query if not done so already
          if (!axios.isCancel(err)) {
            !log?.query && console.trace(`GET ${index ?? catalog?.index}/_search\n${JSON.stringify(body, null, 2)}`)
          }
          rej(err)
        }
      })
    })
  }

  compoundBoolTermsFor = (should: any, mustNot: any, matchAll = false) => {
    let shouldArr = !should || _.isArray(should) ? should : [should],
      mustNotArr = !mustNot || _.isArray(mustNot) ? mustNot : [mustNot]

    if (!_.isEmpty(mustNotArr) && !_.isEmpty(shouldArr)) {
      // Both, need to combine in a psuedo-or clause:
      return {
        bool: {
          minimum_should_match: matchAll ? (should.length + 1) : 1,
          should: [
            ...shouldArr,
            {
              bool: {
                must_not: mustNotArr
              }
            }
          ]
        }
      }
    } else if (!_.isEmpty(mustNot)) {
      return {bool: {must_not: mustNotArr}}
    } else if (!_.isEmpty(should)) {
      return should
    }
  }

  readonly indexes = vipESIndexes

  unwrapHitsResult<T>(input: Partial<EsSearchResponse>): { items: Array<T & { _id: string }>, aggregations, total: { value: number, relation } } {
    return {
      items: input?.hits?.hits?.map(({_source, _id, inner_hits}) => ({_id, ..._source, inner_hits})) ?? [],
      total: input?.hits?.total,
      aggregations: input?.aggregations
    }
  }

  unwrapUnassignedHitsResult<T>({hits: {hits: first, total: unassign_total}}, {hits: {hits: second, total}}): { items: Array<T>, total: { value: number, relation } } {
    return {
      items: first.filter(f => !_.some(second, {_source: {exId: f._source.exId}})),
      total: {value: unassign_total.value - total.value, relation: unassign_total.relation}
    }
  }

  readFieldValue = (data: any, field: string, subfield?: string, opts: { takePer?: number } = {}): IReadFieldResponse => {
    if (!field) {
      //console.warn('No field was specified')
      return null
    }

    //!subfield && _.has(data, field) ? _.get(data, field) :
    let r = this.readRecurse(data, field.split('.'), opts)

    if ((!_.isArray(r) && !_.isNull(r) && !_.isUndefined(r)) || (_.isArray(r) && !_.isEmpty(r))) {
      return _.isArray(r)
             ? _.uniq(r).filter(_.identity).map(v => _.isPlainObject(v) ? {__parent: data, __path: field, ...v} : v)  // uniq is here due to server saving off repeats for enum values
             : _.isPlainObject(r)
               ? {__parent: data, __path: field, ...r}
               : r
    }

    return null
  }

  get strings() { return this.catalog.strings}

  /**
   * Used to parse unknown _source data from es.  Example is style.colorways.name  where colorways is an array
   * @param data
   * @param field
   * @param subfield
   * @param opts
   */
  readField = (data: any, field: string, subfield?: string, opts: { noString?: boolean, takePer?: number } = {}): IReadFieldResponse => {
    const {catalog, strings} = this

    if (!field) {
      //console.warn('No field was specified')
      return {valAsString: strings.noValue, val: null}
    }

    const boardField = catalog?._fieldByName[field],
      fieldExpression = boardField?.computed?.expression,
      r = fieldExpression ?
          fieldExpression.compute(fieldExpression.fields.map(f => this.readFieldValue(data, f, subfield, opts))) :
          this.readFieldValue(data, field, subfield, opts)
    if (_.isNumber(r) || r) {
      return {
        valAsString: opts?.noString ? null : (boardField?.def?.prefix ?? '') + ((_.isArray(r)
                                                                                 ? r.map(v => v && subfield && v[subfield] ? v[subfield] : v).map(s => s === '' ? strings.noValue : s).join(', ')
                                                                                 : boardField?.renderer == 'percentage' ? Number(Number(r) * (boardField?.rendererExtra?.multiplier ?? 1)).toLocaleString(undefined, {style: 'percent', ...(boardField?.rendererExtra || {maximumFractionDigits: 0})})
                                                                                                                        : boardField?.def?.precision ? Number(r).toFixed(boardField.def?.precision) : subfield && r && (r[subfield] != null || r[subfield] != undefined) ? r[subfield].toString() : r?.toString()) ?? r) + (boardField?.def?.suffix ?? ''),

        val: _.isArray(r)
             ? _.uniq(r.map(v => _.isPlainObject(v) ? {__parent: data, __path: field, ...v} : v)).filter(_.identity)
             : _.isPlainObject(r)
               ? {__parent: data, __path: field, ...r}
               : r
      }
    }

    return {valAsString: null, val: null}
  }

  private readRecurse = (parent: any, path: string[], opts?: { takePer?: number }) => {
    const {strings, readRecurse} = this, __path = `${parent?.__path ? `${parent.__path}.` : ''}${path[0]}`

    let child = _.get(parent, path[0])
    if (_.isPlainObject(child)) {
      child = {
        ...child, __path
      }
    } else if (_.isArray(child)) {
      child = child.map(v => _.isPlainObject(v) ? {...v, __parent: parent, __path} : v)
    }

    const remaining = path.slice(1)
    // console.log({parent, path, remaining, child})

    return remaining.length == 0
           ? child === ''
             ? ''
             : _.isArray(child) ? _.take(child, opts?.takePer ?? child.length) : child
           : _.isArray(child)
             ? _.flatMap(child.map(v => readRecurse(v, remaining, opts)))
             : readRecurse(child, remaining, opts)
  }

  @action upsert = async (payLoad: IUpsertInput) => {
    return new Promise((res) => {
      when(() => this.axiosInstance != null, async () => {
        const r = await this.nest.post('es/upsert', payLoad)
        res(r)
      })
    })
  }

  get stores() { return this.catalog.stores}

  @action genericUpsert = async <T = any>({ignoreError, meta, ...payload}: { index: string, _id?: string, body: any, refresh?: boolean, conflicts?: 'abort' | 'proceed', ignoreError?: boolean, meta?: string }, {log}: EsLogParam = {}): Promise<{ data: T & { _id: string } }> => {
    return new Promise((res, rej) => {
      when(() => this.axiosInstance != null, async () => {
        try {
          const {nest, stores: {authStore: {user}}} = this
          const r = await nest.put('es/generic/upsert', _.merge(payload, {body: {updatedBy: user?.id, updatedAt: Date.now()}}))  // TODO - _server_ should be the one doing this
          res(r)
        } catch (err) {
          if (!ignoreError) {
            // Log the query if not done so already
            !log?.query && console.error(`Error running ES upsert:`, {query: `PUT  ${payload?.index ?? this.catalog?.index}/es/generic/upsert${payload._id ? `/${payload._id}` : ''}`}, {meta})
            console.error(err)
            // catalog && (catalog.error = err)
            rej(err)
          }
          res(null)
        }
      })
    })
  }

  @action ping = async () => {
    return new Promise((res) => {
      when(() => this.axiosInstance != null, async () => {
        const r = await this.nest.get('es/ping')
        res(r)
      })
    })
  }

  @action delete = async (id: string, index: string) => {
    const r = await this.nest.post('es/delete', {
      index,
      value: {
        query: {
          bool: {
            minimum_should_match: 1,
            should: [
              {
                match: {
                  id: id
                }
              },
              {
                match: {
                  _id: id
                }
              }
            ]
          }
        }
      }
    })

    // console.log(`es.delete`, {id, index, r})
    return r?.data as ESDeleteResponse
  }

  get nest() { return this.client.axiosNest}

  @action deleteByQuery = async (query: any, index: string) => {
    return await this.nest.post('es/delete', {
      index,
      value: {
        query
      }
    })
  }

  @action assignToField = async (payLoad: IFieldMutationPayLoad) => {
    return await this.nest.post('es/attribute/assign', payLoad)
  }

  @action mutateField = async (payLoad: IFieldMutationPayLoad) => {
    return await this.nest.post('es/attribute/mutate', payLoad)
  }

  @action deleteImageC8AndES = async (payLoad: IDeleteImagePayload) => {
    return await this.nest.post('es/image/delete', payLoad)
  }

  @action reImportImage = async (imageId: string) => {
    return imageId ? await this.nest.get(`image-proxy/reimport/${imageId}`) : null
  }

  // @action put = async (payload: {index: string, _id: string, body: any}) => {
  //   return await es.axiosInstance.post('es/put', payload)
  // }
  //
  //
  // @action createDoc = async (payload: {index: string; _id?: string; body}) => {
  //   return await es.axiosInstance.post('es/createDoc', payload)
  // }

  escapeString(searchText: string) {
    const reserved = ['\\', '+', '=', '&', '|', '>', '<', '!', '(', ')', '{', '}', '[', ']', '^', '"', '~', '*', '?', ':', '/'] // '-' - will be replaced with space
    let r = searchText
    reserved.forEach(c => {
      r = r.split(c).join(`\\${c}`)
    })
    return r
  }

  @action updateDocument = async (payload: IUpdateDocOptions) => {
    return await this.client.updateByQuery(payload)
  }

  /**
   * Unless a special text analyzer is used in ES, things like ()[]-{} etc won't be matched so either strip them or replace them depending on the analyzer in your schema
   * @param searchText
   * @param removeSpecialCharacters
   */
  tokenizeQueryString(searchText: string, removeSpecialCharacters = false) {
    // This prefixes the special char mentioned in regex with \\ so that it can be used in elastic search
    const r = searchText.replace(/\-/g, ' ').replace(/[.+/\-\:\"\!\~?^${}()|[\]\\]/g, removeSpecialCharacters ? '' : '\\$& AND ')
    return r

  }

  // Replaced with change log
  /*get filterForNonDeletedItems() {
    if (!ui.settings?.deleteInfo?.markAsDelete) return null

    const DELETE_FIELD = ui.settings?.deleteInfo?.deleteFieldName || 'ES_hasBeenDeleted'
    return {
      bool: {
        minimum_should_match: 1,
        should: [
          {
            bool: {
              must: [
                {
                  exists: {
                    field: DELETE_FIELD
                  }
                },
                {
                  term: {
                    [DELETE_FIELD]: {
                      value: false
                    }
                  }
                }
              ]
            }
          },
          {
            bool: {
              must_not: [
                {
                  exists: {
                    field: DELETE_FIELD
                  }
                }
              ]
            }
          }
        ]
      }
    }
  }
*/
  /**
   * Combine multiple nested terms together
   * @param terms
   */
  combineNestedTerms = (terms: Array<any>) => {
    if (terms.length <= 1) return terms

    const nestedTerms = _.groupBy(terms.filter(r => r?.nested?.path), r => r.nested.path)
    const r = [
      ...terms.filter(r => !r.nested?.path),  // Regular terms
      // Nested terms
      ..._.keys(nestedTerms).map(k => ({
        nested: {
          path: k,
          query: {bool: {filter: _.flatMap(nestedTerms[k].map(v => v.nested.query.bool.filter))}}
        }
      }))
    ]

    // console.log({nestedTerms, before: terms, after: r})
    return r
  }

  /**
   * Convert result from ElasticSearch composite query, something like:
   *
   * Input:
   * "buckets" : [
   *         {
   *           "key" : {
   *             "collection.id" : "C19953",
   *             "theme.id" : "C21888"
   *           },
   *         }...
   *
   *
   * Output:
   *  'collection.id:C19953-theme.id:C21888
   */
  compositeQueryKeyAsString = (key: { [key: string]: string }) => {
    return _.entries(key).reduce((r, [key, id]) => `${r.length > 0 ? '-' : ''}${r}${key}:${id ? id.toString() : UNASSIGNED}`, '')
  }
}

export interface EsTotal {
  value: number
  relation: 'eq' | 'gt'
}

export interface EsAggregation<T = {}> {
  key: any
  keys?: string[]
  key_as_string?: string
  doc_count: number
  items: { items: Array<T>, total: EsTotal }
}




// WEBPACK FOOTER //
// ./src/stores/elasticsearch/ElasticSearchStore.ts